import { TrendingUp, TrendingDown, Minus, type LucideIcon } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface KpiCardProps {
  label: string;
  value: string;
  trend?: { percentage: number; positiveIsGood: boolean };
  icon: LucideIcon;
  colorClass?: string;
}

export function KpiCard({ label, value, trend, icon: Icon, colorClass }: KpiCardProps) {
  const getTrendColor = () => {
    if (!trend) return "";
    const isNeutral = Math.abs(trend.percentage) < 5;
    if (isNeutral) return "text-muted-foreground";
    const isUp = trend.percentage > 0;
    return (isUp === trend.positiveIsGood) ? "text-success" : "text-destructive";
  };

  const getTrendIcon = () => {
    if (!trend) return null;
    const isNeutral = Math.abs(trend.percentage) < 5;
    if (isNeutral) return <Minus className="h-3 w-3" />;
    return trend.percentage > 0
      ? <TrendingUp className="h-3 w-3" />
      : <TrendingDown className="h-3 w-3" />;
  };

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-center gap-2 mb-1">
          <Icon className={cn("h-3.5 w-3.5", colorClass || "text-muted-foreground")} />
          <span className="text-xs text-muted-foreground font-medium truncate">{label}</span>
        </div>
        <div className={cn("text-lg sm:text-xl font-bold truncate", colorClass)}>
          {value}
        </div>
        {trend && (
          <div className={cn("flex items-center gap-1 mt-1", getTrendColor())}>
            {getTrendIcon()}
            <span className="text-xs font-medium">
              {Math.abs(trend.percentage).toFixed(0)}%
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
